import type { ReactNode } from "react";

import type { WritingAssist } from "@/shared/writing-assistant/use-writing-assist";
import { WritingAssistButton } from "@/shared/writing-assistant/writing-assist-button";
import { ASSIST_MIN_INPUT } from "@/shared/writing-assistant/writing-assistant.schema";

interface Props {
  /// Caller passes `useWritingAssistGate().canAssist` (permission AND
  /// credits). When false with no `disabledReason` the action is absent
  /// entirely (a permission-only block: no sparkle button for users who
  /// can't use it at all).
  canAssist: boolean;
  /// Current textarea value; the button stays disabled until it has enough
  /// trimmed characters to send (ASSIST_MIN_INPUT).
  value: string;
  assist: WritingAssist;
  /// Set when `canAssist` is false specifically because of the AI credit
  /// pool (see `useWritingAssistGate`). Unlike a permission block, a
  /// credits block still renders the button, just disabled with this
  /// tooltip, since the caller is otherwise entitled to the feature.
  disabledReason?: string;
}

/// Label-row action for the writing-assistant textareas: gated sparkle button
/// with the shared too-short tooltip. Shared by the feedback and kudos compose
/// dialogs so the gate + threshold copy live in one place.
export function WritingAssistLabelAction({
  canAssist,
  value,
  assist,
  disabledReason,
}: Props): ReactNode {
  if (!canAssist && !disabledReason) return null;
  const tooShort = value.trim().length < ASSIST_MIN_INPUT;
  const tooShortReason = tooShort ? "Jot down a few rough notes first." : undefined;
  return (
    <WritingAssistButton
      disabled={!canAssist || tooShort}
      generating={assist.isGenerating}
      disabledReason={disabledReason ?? tooShortReason}
      onClick={() => void assist.generate()}
    />
  );
}
